import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

interface Currency {
  id: string;
  code: string;
  symbol: string;
  rate: number;
}

interface StoreSettings {
  id: string;
  default_currency_id: string;
  default_tax_rate_id: string;
  show_prices_with_tax: boolean;
  store_name?: string;
  store_email?: string;
  store_phone?: string;
  store_address?: string;
  currency?: string;
  /** BCP 47 oznaka jezika prodavnice, npr. "sr-RS". */
  language?: string;
  hero_section?: {
    title: string;
    subtitle: string;
    image_url: string;
  };
  social_media?: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
  };
  footer_content?: {
    company_info: {
      about: string;
      address: string;
      phone: string;
      email: string;
    };
    quick_links: Array<{
      title: string;
      url: string;
    }>;
    social_links: {
      facebook: string;
      instagram: string;
      twitter: string;
      linkedin: string;
    };
    bottom_text: string;
  };
  header_content?: {
    logo: {
      url: string;
      alt: string;
      width: number;
    };
    navigation: Array<{
      id: string;
      title: string;
      url: string;
      is_active: boolean;
    }>;
    displayCartIcon?: boolean;
    displaySearchIcon?: boolean;
    showBlog?: boolean;
    categoryQuickAdd?: boolean;
  };
  updated_at?: string;
}

export const useStoreSettings = () => {
  const [settings, setSettings] = useState<StoreSettings | null>(null);
  const [defaultCurrency, setDefaultCurrency] = useState<Currency | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        console.log('Fetching settings...');
        const { data: settingsData, error: settingsError } = await supabase
          .from('store_settings')
          .select('*')
          .single();

        if (settingsError) throw settingsError;

        if (settingsData) {
          console.log('Settings loaded:', settingsData);
          setSettings(settingsData);

          // Dobavi valutu na osnovu default_currency_id
          if (settingsData.default_currency_id) {
            const { data: currencyData, error: currencyError } = await supabase
              .from('currencies')
              .select('*')
              .eq('id', settingsData.default_currency_id)
              .single();

            console.log('Currency data:', currencyData);
            if (currencyError) {
              console.error('Error fetching default currency:', currencyError);
              return;
            }
            
            console.log('Default currency:', currencyData);
            setDefaultCurrency(currencyData);
          } else {
            // Ako nemamo default_currency_id, probaj da nađeš valutu koja je označena kao default
            const { data: defaultCurrencyData, error: defaultCurrencyError } = await supabase
              .from('currencies')
              .select('*')
              .eq('is_default', true)
              .single();

            if (!defaultCurrencyError && defaultCurrencyData) {
              console.log('Found default currency:', defaultCurrencyData);
              setDefaultCurrency(defaultCurrencyData);
            }
          }
        }
      } catch (error) {
        console.error('Error in useStoreSettings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const formatPrice = (price: number) => {
    if (!defaultCurrency) {
      console.log('No default currency found, using USD');
      return `$${price.toFixed(2)}`;
    }

    console.log('Formatting price with currency:', defaultCurrency);
    const convertedPrice = price * defaultCurrency.rate;
    
    // Formatiramo broj sa separatorima za hiljade
    const formattedNumber = convertedPrice.toLocaleString('sr-RS', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    
    // Za RSD stavljamo simbol nakon cene
    if (defaultCurrency.code === 'RSD') {
      return `${formattedNumber} ${defaultCurrency.symbol}`;
    }
    
    // Za ostale valute stavljamo simbol ispred cene
    return `${defaultCurrency.symbol}${formattedNumber}`;
  };

  return {
    settings,
    defaultCurrency,
    loading,
    formatPrice
  };
}; 